import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
	useGetAllTeamMembersForCustomerQuery,
	useDeleteUserTeamMemberMutation,
	useNewTeamMemberInvitationMutation,
} from "../../../service/team-management-service";
import { UserTeamMemberResponse } from "../../../service/contract/team-management";
import { InviteTeamMemberModal } from "./InviteTeamMemberModal";
import styles from "./shared-account.module.scss";
import { TeamMembersIcon } from "../../../assets/icons";

interface Props {
	onSkip: () => void;
	onComplete: () => void;
}

export const SharedAccountManagePage: React.FC<Props> = ({ onSkip, onComplete }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [openMenuId, setOpenMenuId] = useState<string | null>(null);

	const { data: members = [], isLoading } = useGetAllTeamMembersForCustomerQuery();
	const [deleteMember] = useDeleteUserTeamMemberMutation();
	const [resendInvitation] = useNewTeamMemberInvitationMutation();

	useEffect(() => {
		if (!openMenuId) return;
		const close = () => setOpenMenuId(null);
		document.addEventListener("click", close);
		return () => document.removeEventListener("click", close);
	}, [openMenuId]);

	const handleResend = async (member: UserTeamMemberResponse) => {
		setOpenMenuId(null);
		try {
			await resendInvitation({
				email: member.email,
				firstName: member.firstName,
				lastName: member.lastName,
				roleId: member.role.id,
			}).unwrap();
			toast.success("Invitation resent");
		} catch {
			toast.error("Failed to resend invitation");
		}
	};

	const handleDelete = async (userReferenceId: string) => {
		setOpenMenuId(null);
		try {
			await deleteMember({ userReferenceId }).unwrap();
		} catch {
			toast.error("Failed to remove member");
		}
	};

	return (
		<div className={styles.stepContent}>
			<div className={styles.card}>
				<div className={styles.cardHeader}>
					<div>
						<div className={styles.cardLabel}>Invited Members</div>
						{members.length > 0 && (
							<div className={styles.memberSubtitle}>{members.length} member{members.length !== 1 ? "s" : ""} invited</div>
						)}
					</div>
					<button className={styles.inviteHeaderBtn} onClick={() => setIsModalOpen(true)}>
						Invite Member +
					</button>
				</div>

				{isLoading ? (
					<div className={styles.emptyState}>
						<span className={styles.emptyStateText}>Loading...</span>
					</div>
				) : members.length === 0 ? (
					<div className={styles.emptyState}>
						<TeamMembersIcon />
						<span className={styles.emptyStateText}>No team members yet</span>
						<span className={styles.emptyStateSubText}>Invite members to start collaborating</span>
					</div>
				) : (
					<div className={styles.memberList}>
						{members.map(member => (
							<div key={member.id} className={styles.memberItem}>
								<div className={styles.memberAvatar}>
									{member.image ? (
										<img src={member.image} alt="" className={styles.memberAvatarImg} />
									) : (
										<span className={styles.memberAvatarInitials}>
											{member.firstName[0]}{member.lastName[0]}
										</span>
									)}
								</div>
								<div className={styles.memberInfo}>
									<span className={styles.memberName}>{member.firstName} {member.lastName}</span>
									<span className={styles.memberRole}>{member.role?.name}</span>
								</div>
								<div
									className={styles.memberActions}
									onClick={e => e.stopPropagation()}
								>
									<button
										className={styles.menuBtn}
										onClick={() => setOpenMenuId(openMenuId === member.id ? null : member.id)}
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
											<circle cx="5" cy="12" r="2" />
											<circle cx="12" cy="12" r="2" />
											<circle cx="19" cy="12" r="2" />
										</svg>
									</button>
									{openMenuId === member.id && (
										<div className={styles.memberMenu}>
											<button className={styles.menuItem} onClick={() => handleResend(member)}>
												<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
													<path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
													<path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
												Resend Invitation
											</button>
											<button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={() => handleDelete(member.id)}>
												<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
													<polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
													<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
													<path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
												Remove Member
											</button>
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
			
			<div className={styles.buttonSection}>
				
			<button className={styles.continueButton} onClick={onComplete}>Save & Continue</button>
			<button className={styles.skipButton} onClick={onSkip}>Skip for now</button>
			</div>


			<InviteTeamMemberModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
		</div>
	);
};
